import React from 'react'
import {
  RealEstateCategory,
  RealEstateSiteRecommendedUseTypes,
  SegmentationType,
  TransactionType,
} from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { AGENT_INDUSTRIAL_PLOT_SELL_FLOW_PATH } from '../../../../../services/routes/routes'
import { agentIndustrialPlotSellSchema } from './agentIndustrialPlotSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  areaCoverage,
  constructibleAfter,
  freeFrom,
  gfzField,
  grzField,
  siteRecommendedUseTypes,
  usageType,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  courtageGroup,
  plotAreaField,
  priceField,
  purchasePriceOnRequestToggle,
  separableSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { UsageTypeOption } from '../../../../../@types/dropdown-types'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'

const agentIndustrialPlotSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isInternationalAddressOption={true} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_NUMBERS],
    element: (props) => <ObjectNumbersScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        properties={[
          plotAreaField('left', undefined),
          separableSpaceField(SeparableSpaceType.AREA, 'right', undefined),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          freeFrom(getDefaultClasses('right')),
          purchasePriceOnRequestToggle(),
          courtageGroup(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => (
      <Description
        {...props}
        hasOtherNote={true}
        hasLocationNote={true}
        initialPlaceholder={['wizard.screen.description.adDescriptionPlaceholderPlot', 'eg. central.']}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
        allowMultimediaLinks
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.plotIndustrialPlotSell'
        properties={[
          usageType(),
          grzField(getDefaultClasses('left')),
          gfzField(getDefaultClasses('right')),
          areaCoverage(),
          constructibleAfter(),
          siteRecommendedUseTypes([
            RealEstateSiteRecommendedUseTypes.FARMLAND,
            RealEstateSiteRecommendedUseTypes.FUTURE_DEVELOPMENT_LAND,
            RealEstateSiteRecommendedUseTypes.MOORAGE,
            RealEstateSiteRecommendedUseTypes.OFFICE,
            RealEstateSiteRecommendedUseTypes.CAMPING,
            RealEstateSiteRecommendedUseTypes.BIG_STORE,
            RealEstateSiteRecommendedUseTypes.LITTLE_STORE,
            RealEstateSiteRecommendedUseTypes.GARAGE,
            RealEstateSiteRecommendedUseTypes.GARDEN,
            RealEstateSiteRecommendedUseTypes.GASTRONOMY,
            RealEstateSiteRecommendedUseTypes.BUSINESS,
            RealEstateSiteRecommendedUseTypes.HOTEL,
            RealEstateSiteRecommendedUseTypes.INDUSTRY,
            RealEstateSiteRecommendedUseTypes.NO_DEVELOPMENT,
            RealEstateSiteRecommendedUseTypes.SMALL_BUSINESS,
            RealEstateSiteRecommendedUseTypes.STOCK,
            RealEstateSiteRecommendedUseTypes.ORCHARD,
            RealEstateSiteRecommendedUseTypes.CAR_PARK,
            RealEstateSiteRecommendedUseTypes.PRODUCTION,
            RealEstateSiteRecommendedUseTypes.PARKING_SPACE,
            RealEstateSiteRecommendedUseTypes.FORREST,
          ]),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager />,
  },
]

export const agentIndustrialPlotSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.industrialPlot',
    offerType: 'wizard.navigation.header.sells',
    path: AGENT_INDUSTRIAL_PLOT_SELL_FLOW_PATH,
    name: FlowName.AGENT_INDUSTRIAL_PLOT_SELL,
    validationSchema: agentIndustrialPlotSellSchema,
    priceToApiMapper: priceToApiSell,
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
    metaData: {
      category: RealEstateCategory.INDUSTRIAL_PLOT,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false, usageType: UsageTypeOption.TRADE },
  flowScreens: agentIndustrialPlotSellFlowScreens,
}
